/**
 * Service for importing products from CSV/Excel files
 */

import * as XLSX from 'xlsx';
import { z } from 'zod';
import { productService } from './productService';
import type {
  ImportedProduct,
  ImportValidationResult,
  ImportOptions,
  ImportExecutionResult,
  ImportWarning,
  ImportError,
} from '@/types/import.types';

// Validation schema for imported products
const importProductSchema = z.object({
  nom: z.string().min(2, 'Nom requis (min 2 caractères)'),
  categorie: z.string().optional().default('autre'),
  unite: z.enum(['kg', 'g', 'L', 'ml', 'unite', 'piece', 'bouteille', 'boite', 'sachet']).optional().default('unite'),
  seuil_alerte: z.coerce.number().min(0).optional().default(10),
  seuil_critique: z.coerce.number().min(0).optional().default(5),
  prix_unitaire: z.coerce.number().min(0).optional(),
  code_barre: z.string().optional(),
});

// Valid categories
const VALID_CATEGORIES = new Set([
  'legumes',
  'fruits',
  'viandes',
  'poissons',
  'produits_laitiers',
  'epicerie',
  'boissons',
  'surgeles',
  'boulangerie',
  'condiments',
  'conserves',
  'autre',
]);

/**
 * Parse a file (CSV or Excel) and return raw data
 */
export async function parseFile(file: File): Promise<Record<string, string>[]> {
  const extension = file.name.split('.').pop()?.toLowerCase();

  if (extension === 'csv') {
    return parseCSV(file);
  } else if (extension === 'xlsx' || extension === 'xls') {
    return parseExcel(file);
  }

  throw new Error('Format de fichier non supporté. Utilisez .csv ou .xlsx');
}

/**
 * Parse a CSV file
 */
async function parseCSV(file: File): Promise<Record<string, string>[]> {
  const text = await file.text();
  const lines = text.split('\n').filter(line => line.trim());

  if (lines.length < 2) {
    throw new Error('Le fichier CSV est vide ou invalide');
  }

  const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
  const rows: Record<string, string>[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    const row: Record<string, string> = {};

    headers.forEach((header, index) => {
      row[header] = values[index]?.trim() || '';
    });

    // Skip empty rows
    if (row['nom'] || row['name']) {
      rows.push(row);
    }
  }

  return rows;
}

/**
 * Parse a CSV line handling quoted values
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
}

/**
 * Parse an Excel file
 */
async function parseExcel(file: File): Promise<Record<string, string>[]> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });

  // Look for "Produits" sheet or use the first one
  const sheetName = workbook.SheetNames.find(
    name => name.toLowerCase() === 'produits'
  ) || workbook.SheetNames[0];

  const sheet = workbook.Sheets[sheetName];
  const data = XLSX.utils.sheet_to_json<Record<string, string>>(sheet, { defval: '' });

  // Normalize headers to lowercase
  return data.map(row => {
    const normalized: Record<string, string> = {};
    Object.entries(row).forEach(([key, value]) => {
      normalized[key.toLowerCase().trim()] = String(value);
    });
    return normalized;
  });
}

/**
 * Validate imported data and categorize results
 */
export async function validateImport(
  data: Record<string, string>[],
  restaurantId: string,
  options: ImportOptions
): Promise<ImportValidationResult> {
  const result: ImportValidationResult = {
    success: [],
    warnings: [],
    errors: [],
  };

  // Get existing products to detect duplicates
  const existingProducts = await productService.getProducts(restaurantId);
  const existingNames = new Set(
    existingProducts.data?.map(p => p.nom.toLowerCase()) || []
  );

  for (let i = 0; i < data.length; i++) {
    const row = data[i];

    // Map common column name variations
    const normalizedRow = normalizeColumnNames(row);

    try {
      const validated = importProductSchema.parse(normalizedRow);

      // Check for duplicates
      if (existingNames.has(validated.nom.toLowerCase())) {
        if (options.skipDuplicates) {
          result.warnings.push({
            product: validated,
            message: 'Doublon ignoré - produit existant',
          });
          continue;
        } else if (!options.updateExisting) {
          result.errors.push({
            row: i + 2, // +2 for header and 0-indexing
            data: row,
            message: `Le produit "${validated.nom}" existe déjà`,
          });
          continue;
        }
      }

      // Check category
      const categoryLower = validated.categorie?.toLowerCase().replace(/é/g, 'e').replace(/ /g, '_');
      if (categoryLower && !VALID_CATEGORIES.has(categoryLower)) {
        if (options.createCategories) {
          result.warnings.push({
            product: validated,
            message: `Catégorie "${validated.categorie}" sera mappée vers "autre"`,
          });
          validated.categorie = 'autre';
        } else {
          validated.categorie = 'autre';
          result.warnings.push({
            product: validated,
            message: 'Catégorie inconnue, remplacée par "autre"',
          });
        }
      } else if (categoryLower) {
        validated.categorie = categoryLower;
      }

      result.success.push(validated as ImportedProduct);
    } catch (error) {
      if (error instanceof z.ZodError) {
        result.errors.push({
          row: i + 2,
          data: row,
          message: error.errors.map(e => e.message).join(', '),
        });
      } else {
        result.errors.push({
          row: i + 2,
          data: row,
          message: String(error),
        });
      }
    }
  }

  return result;
}

/**
 * Normalize column names to handle variations
 */
function normalizeColumnNames(row: Record<string, string>): Record<string, string> {
  const normalized: Record<string, string> = {};

  // Column name mappings
  const mappings: Record<string, string[]> = {
    nom: ['nom', 'name', 'produit', 'product', 'designation'],
    categorie: ['categorie', 'category', 'cat', 'type'],
    unite: ['unite', 'unit', 'unité'],
    seuil_alerte: ['seuil_alerte', 'alert', 'alert_threshold', 'alerte'],
    seuil_critique: ['seuil_critique', 'critical', 'critical_threshold', 'critique'],
    prix_unitaire: ['prix_unitaire', 'price', 'prix', 'cout', 'cost'],
    code_barre: ['code_barre', 'barcode', 'ean', 'upc', 'code'],
  };

  Object.entries(mappings).forEach(([targetKey, sourceKeys]) => {
    for (const sourceKey of sourceKeys) {
      if (row[sourceKey] !== undefined && row[sourceKey] !== '') {
        normalized[targetKey] = row[sourceKey];
        break;
      }
    }
  });

  return normalized;
}

/**
 * Execute the import of validated products
 */
export async function executeImport(
  products: ImportedProduct[],
  restaurantId: string,
  options: ImportOptions
): Promise<ImportExecutionResult> {
  let imported = 0;
  let updated = 0;
  let failed = 0;

  // Get existing products for update check
  const existingProducts = await productService.getProducts(restaurantId);
  const existingByName = new Map(
    existingProducts.data?.map(p => [p.nom.toLowerCase(), p]) || []
  );

  for (const product of products) {
    try {
      const existing = existingByName.get(product.nom.toLowerCase());

      if (existing && options.updateExisting) {
        // Update existing product
        const updateResult = await productService.updateProduct(existing.id, {
          categorie: product.categorie,
          unite: product.unite,
          seuil_alerte: product.seuil_alerte,
          seuil_critique: product.seuil_critique,
          cout_unitaire: product.prix_unitaire,
          code_barre: product.code_barre,
        });

        if (updateResult.success) {
          updated++;
        } else {
          failed++;
        }
      } else if (!existing) {
        // Create new product
        const createResult = await productService.createProduct(restaurantId, {
          nom: product.nom,
          categorie: product.categorie,
          unite: product.unite,
          seuil_alerte: product.seuil_alerte,
          seuil_critique: product.seuil_critique,
          cout_unitaire: product.prix_unitaire,
          code_barre: product.code_barre,
        });

        if (createResult.success) {
          imported++;
        } else {
          console.error(`Failed to import ${product.nom}:`, createResult.error);
          failed++;
        }
      }
    } catch (error) {
      console.error(`Failed to import ${product.nom}:`, error);
      failed++;
    }
  }

  return { imported, updated, failed };
}

/**
 * Generate a template file for download
 */
export function generateTemplate(format: 'csv' | 'xlsx'): Blob {
  const headers = ['nom', 'categorie', 'unite', 'seuil_alerte', 'seuil_critique', 'prix_unitaire', 'code_barre'];
  const examples = [
    ['Tomates cerises', 'legumes', 'kg', '5', '2', '4.50', '3760001234567'],
    ['Farine T55', 'epicerie', 'kg', '10', '5', '1.20', ''],
    ['Lait entier', 'produits_laitiers', 'L', '8', '4', '1.10', ''],
  ];

  if (format === 'csv') {
    const content = [
      headers.join(','),
      ...examples.map(row => row.join(',')),
    ].join('\n');
    return new Blob(['\ufeff' + content], { type: 'text/csv;charset=utf-8' }); // BOM for Excel
  } else {
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...examples]);

    // Set column widths
    worksheet['!cols'] = [
      { wch: 20 }, // nom
      { wch: 15 }, // categorie
      { wch: 8 },  // unite
      { wch: 12 }, // seuil_alerte
      { wch: 14 }, // seuil_critique
      { wch: 12 }, // prix_unitaire
      { wch: 15 }, // code_barre
    ];

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Produits');
    const buffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
    return new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  }
}

/**
 * Download a template file
 */
export function downloadTemplate(format: 'csv' | 'xlsx'): void {
  const blob = generateTemplate(format);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `modele_import_produits.${format}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
